import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  doc,
  updateDoc,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { COLLECTIONS, splitPath } from '@elka/firebase';
import type { WorkerNotification } from '@elka/types';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { syncWrite } from '@/lib/offline/syncWrite';

export function useNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<WorkerNotification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [claimsReady, setClaimsReady] = useState(false);

  // Verify the user's workerRole claim has propagated before subscribing — Firestore rules
  // require it, and a listener that fires before the token refresh trips the error branch
  // and flashes "שגיאה בטעינת הודעות" on first load.
  useEffect(() => {
    if (!user) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setClaimsReady(false);
      return;
    }
    let cancelled = false;
    user
      .getIdTokenResult()
      .then((res) => {
        if (!cancelled) setClaimsReady(res.claims.workerRole === 'worker');
      })
      .catch(() => {
        if (!cancelled) setClaimsReady(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!user) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setNotifications([]);

      setIsLoading(false);
      return;
    }

    if (!claimsReady) return;

    const ref = collection(db, ...splitPath(COLLECTIONS.team.notifications));
    const q = query(
      ref,
      where('recipientUid', 'in', [user.uid, 'all']),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as WorkerNotification);
        setNotifications(docs);
        setIsLoading(false);
        setError(null);
      },
      () => {
        setError('שגיאה בטעינת הודעות');
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user, claimsReady]);

  const unreadCount = useMemo(() => {
    if (!user) return 0;
    return notifications.filter((n) => !n.readBy?.[user.uid]).length;
  }, [notifications, user]);

  // Sync unread count to PWA app icon badge
  useEffect(() => {
    if ('setAppBadge' in navigator) {
      if (unreadCount > 0) {
        navigator.setAppBadge(unreadCount);
      } else {
        navigator.clearAppBadge();
      }
    }
  }, [unreadCount]);

  const markAsRead = useCallback(
    async (notificationId: string) => {
      if (!user) return;
      const docRef = doc(db, ...splitPath(COLLECTIONS.team.notifications), notificationId);
      await syncWrite(
        updateDoc(docRef, {
          [`readBy.${user.uid}`]: serverTimestamp(),
        })
      );
    },
    [user]
  );

  const markAllAsRead = useCallback(async () => {
    if (!user) return;
    const unread = notifications.filter((n) => !n.readBy?.[user.uid]);
    if (unread.length === 0) return;
    const batch = writeBatch(db);
    unread.forEach((n) => {
      const docRef = doc(db, ...splitPath(COLLECTIONS.team.notifications), n.id);
      batch.update(docRef, { [`readBy.${user.uid}`]: serverTimestamp() });
    });
    await syncWrite(batch.commit());
  }, [user, notifications]);

  return { notifications, isLoading, error, unreadCount, markAsRead, markAllAsRead };
}
